import { StorageService } from "src/app/services/storage.service";
import { isFormulaBase } from "src/app/utils/orders.util";

/**
 * Parses a feeding interval string into an equivalent number of hours.
 *
 * Supported formats:
 *  - "q3h"                     → 3
 *  - "Q4H"                     → 4
 *  - "Every 3 hours"           → 3
 *  - "continuous"              → 1
 *  - "ad lib"                  → null  (no fixed interval)
 *  - "q3h ad lib"              → 3     (numeric interval takes precedence)
 *  - unrecognised              → null
 */
const INTERVAL_REGEXES = {
	q: /q(\d+)h/i,
	every: /every\s+(\d+)\s+hours?/i,
	continuous: /continuous/i,
	adLib: /ad\s*lib/i,
};

export function parseIntervalToHours(interval: string): number | null {
	if (!interval) return null;

	const qMatch = interval.match(INTERVAL_REGEXES.q);
	if (qMatch) return Number(qMatch[1]);

	const everyMatch = interval.match(INTERVAL_REGEXES.every);

	if (everyMatch) return Number(everyMatch[1]);

	if (INTERVAL_REGEXES.continuous.test(interval)) return 1;

	if (INTERVAL_REGEXES.adLib.test(interval)) return null;

	return null;
}

/**
 * Returns how many complete feeds fit within a prep timeframe.
 *
 * @param prepTimeframeHours  - hours available to prepare feeds (e.g. 12)
 * @param intervalHours       - result of parseIntervalToHours (e.g. 3)
 */
export function calculateNumberOfFeeds(
	prepTimeframeHours: number,
	intervalHours: number,
): number {
	return Math.floor(prepTimeframeHours / intervalHours);
}

export function getPrepTimeframeHours(base: string | undefined): number | null {
	const settings = StorageService.configs?.productSettings;
	if (settings == null) return null;
	return !isFormulaBase(base)
		? settings.prepTimeframeHumanMilk ?? null
		: settings.prepTimeframeFormula ?? null;
}